import React, { SyntheticEvent, useEffect, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader, useNotify } from '@routes-plan/ui';
import { Country } from '../../../models/country';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { ScreenTourButton } from '../../../components/help/ScreenHelp';
import { AdminPage, FormActions, FormSection, apiError } from '../shared/AdminUi';

/** Crear o editar un banco: nombre y país. */
const BankFormPage: React.FC<{ mode: 'new' | 'edit' }> = ({ mode }) => {
  const t = useT();
  const notify = useNotify();
  const navigate = useNavigate();
  const { id } = useParams();
  const { canCreate, canUpdate } = useResourcePermissions('/admin/banks');
  const allowed = mode === 'new' ? canCreate : canUpdate;
  const [name, setName] = useState('');
  const [countryId, setCountryId] = useState('');
  const [countries, setCountries] = useState<Country[]>([]);
  const [initial, setInitial] = useState({ name: '', countryId: '' });
  const [errors, setErrors] = useState<{ name?: string; countryId?: string }>({});
  const [saving, setSaving] = useState(false);

  setPageTitle(mode === 'new' ? 'banks.newTitle' : 'banks.editTitle');
  useEffect(() => {
    const fetchData = async () => {
      try {
        const requests = [axios.get('typecountry'), ...(mode === 'edit' ? [axios.get(`banks/${id}`)] : [])];
        const [resCountries, resBank] = await Promise.all(requests);
        setCountries(Array.isArray(resCountries?.data?.data) ? resCountries.data.data : []);
        if (resBank) {
          const bank = resBank.data?.data;
          const loaded = { name: bank?.name ?? '', countryId: bank?.country?.id != null ? String(bank.country.id) : '' };
          setName(loaded.name);
          setCountryId(loaded.countryId);
          setInitial(loaded);
        }
      } catch (error) {
        notify.error(mode === 'new' ? t('banks.countriesLoadError') : t('banks.editLoadError'));
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, mode]);

  const dirty = name !== initial.name || countryId !== initial.countryId;

  const submit = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (!allowed) return;
    const found: typeof errors = {};
    if (!name.trim()) found.name = t('common.required');
    if (!countryId) found.countryId = t('common.required');
    setErrors(found);
    if (Object.keys(found).length) {
      notify.warning(t('banks.requiredFields'));
      return;
    }
    setSaving(true);
    try {
      if (mode === 'new') {
        await axios.post('banks', { name, countryId: Number(countryId) });
        notify.success(t('banks.createdOk'));
      } else {
        await axios.put('banks', { id, name, countryId: Number(countryId) });
        notify.success(t('banks.updatedOk'));
      }
      navigate('/admin/banks');
    } catch (error) {
      notify.error(apiError(error, mode === 'new' ? t('banks.createError') : t('banks.updateError')));
    } finally {
      setSaving(false);
    }
  };

  const title = mode === 'new' ? t('banks.newTitle') : t('banks.editTitle');
  return (
    <AdminPage narrow>
      <PageHeader
        title={title}
        description={t('banks.formSubtitle')}
        breadcrumbs={[{ label: t('banks.title'), href: '#/admin/banks' }, { label: title }]}
        helpAction={<ScreenTourButton />}
      />
      <Box component="form" noValidate onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormSection title={t('banks.sectionData')}>
          <TextField
            id="name"
            data-tour="bank-name"
            label={t('banks.bankName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            required
            fullWidth
          />
          <TextField
            id="typeCountryId"
            data-tour="bank-country"
            select
            label={t('banks.country')}
            value={countryId}
            onChange={(e) => setCountryId(e.target.value)}
            error={!!errors.countryId}
            helperText={errors.countryId}
            required
            fullWidth
          >
            {countries.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
        </FormSection>
        <FormActions
          cancelTo="/admin/banks"
          saving={saving}
          allowed={allowed}
          deniedTooltip={mode === 'new' ? t('common.noPermissionCreate') : t('common.noPermissionEdit')}
          disabled={!dirty}
        />
      </Box>
    </AdminPage>
  );
};

export default BankFormPage;
